import React from 'react';
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

interface MultiTurnActionProps {
  actionValue?: any[];
}

const formatFieldName = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());

const getTurnValues = (turn: any) => {
  if (Array.isArray(turn?.fields)) {
    return turn.fields.map((field: any, index: number) => ({
      key: field?.key || String(index),
      value: field?.value ?? '',
    }));
  }

  return Object.entries(turn ?? {})
    .filter(([key]) => key !== 'index')
    .map(([key, value]) => ({ key, value }));
};

const MultiTurnAction: React.FC<MultiTurnActionProps> = ({ actionValue }) => (
  <Stack spacing={2}>
    {actionValue?.map((action: any, actionIndex: number) => {
      const configuredFields = action?.config_details?.value?.value ?? [];
      const fieldLabels = new Map(
        configuredFields.map((field: any) => [field.key, field.title || field.key])
      );
      const turns = action?.turns ?? [];
      const heading = action?.config_details?.value?.label || action?.config_details?.value?.title || 'Multi Turn';

      return (
        <Box key={action?.id || actionIndex}>
          <Typography sx={{ mb: 1, fontWeight: 700, color: '#10383D' }}>
            {heading}
          </Typography>
          {turns.length ? (
            <Stack spacing={1.5}>
              {turns.map((turn: any, turnIndex: number) => {
                const values = getTurnValues(turn);
                return (
                  <Paper key={turn?.index ?? turnIndex} variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: '#f6f8fa' }}>
                      <Typography variant="subtitle2" fontWeight={700}>Turn {turn?.index ?? turnIndex + 1}</Typography>
                      <Chip size="small" label={`${values.length} field${values.length === 1 ? '' : 's'}`} />
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5, p: 1.5 }}>
                      {values.map(({ key, value }: any) => (
                        <Box key={key} sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary">
                            {fieldLabels.get(key) || formatFieldName(key)}
                          </Typography>
                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                            {value ? String(value) : 'Not provided'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">No multi-turn data available.</Typography>
          )}
        </Box>
      );
    })}
  </Stack>
);

export default MultiTurnAction;